import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTimezone } from "@/contexts/timezone-context";
import { api } from "@/lib/api";
import { TOAST_ERROR_DURATION_MS, TOAST_SUCCESS_DURATION_MS } from "@/lib/toast-durations";
import type { BatchActionName, Machine, MachineRequest } from "@/lib/types";

function machineLabel(name: string | undefined, id: number): string {
  return name ?? `machine #${id}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Invalidate both machine query keys (pool + reserved-capacity CBGs). Exported
 * for the acquire flows, which refresh the table without a mutation observer.
 */
export function invalidateMachines(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["machines"] });
  void qc.invalidateQueries({ queryKey: ["cbg-machines"] });
}

export function useMachines(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["machines", params],
    queryFn: () => api.listMachines(params),
  });
}

// Single/batch acquire don't use a useMutation hook — the dialogs fire a bare
// promise (so the toast outlives the unmount) and call invalidateMachines().

export function useReleaseMachine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; name?: string }) => api.releaseMachine(id),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      toast.success(`Released ${machineLabel(variables.name ?? data.name, data.id)}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      toast.error(`Failed to release ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useStopMachine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      deleteAfterStop,
    }: {
      id: number;
      deleteAfterStop?: boolean;
      name?: string;
    }) => api.stopMachine(id, deleteAfterStop),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      const verb = variables.deleteAfterStop ? "Stopping + deleting" : "Stopping";
      toast.success(`${verb} ${machineLabel(variables.name ?? data.name, data.id)}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      const verb = variables.deleteAfterStop ? "stop + delete" : "stop";
      toast.error(`Failed to ${verb} ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useStartMachine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; name?: string }) => api.startMachine(id),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      toast.success(`Starting ${machineLabel(variables.name ?? data.name, data.id)}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      toast.error(`Failed to start ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useTransferMachine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      userId,
    }: {
      id: number;
      userId: number;
      name?: string;
      targetName?: string;
    }) => api.transferMachine(id, userId),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      const target = variables.targetName ? ` to ${variables.targetName}` : "";
      toast.success(`Transferred ${machineLabel(variables.name ?? data.name, data.id)}${target}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      toast.error(`Failed to transfer ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useSetNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ machineIds, note }: { machineIds: number[]; note: string | null }) =>
      api.setNote(machineIds, note),
    onSuccess: (data, variables) => {
      invalidateMachines(qc);
      // Report what actually succeeded (partial-permission failures count out).
      const count = data.succeeded;
      const cleared = (variables.note ?? "").trim().length === 0;
      const verb = cleared ? "Cleared note on" : "Set note on";
      const noun = `${count} machine${count === 1 ? "" : "s"}`;
      toast.success(`${verb} ${noun}`, { duration: TOAST_SUCCESS_DURATION_MS });
    },
    onError: (error) => {
      toast.error("Failed to update note", {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useToggleEodReleaseSkip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean; name?: string }) =>
      enabled ? api.skipEodRelease(id) : api.unskipEodRelease(id),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      const label = machineLabel(variables.name ?? data.name, data.id);
      toast.success(
        variables.enabled
          ? `${label} will skip tonight's EOD release`
          : `EOD release skip cleared for ${label}`,
        { duration: TOAST_SUCCESS_DURATION_MS },
      );
    },
    onError: (error, variables) => {
      const verb = variables.enabled ? "enable" : "disable";
      toast.error(`Failed to ${verb} EOD skip for ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useToggleMachineLock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean; name?: string }) =>
      enabled ? api.lockMachine(id) : api.unlockMachine(id),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      const label = machineLabel(variables.name ?? data.name, data.id);
      toast.success(variables.enabled ? `Locked ${label}` : `Unlocked ${label}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      const verb = variables.enabled ? "lock" : "unlock";
      toast.error(`Failed to ${verb} ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useToggleMachineDisposable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean; name?: string }) =>
      enabled ? api.markMachineDisposable(id) : api.unmarkMachineDisposable(id),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      const label = machineLabel(variables.name ?? data.name, data.id);
      toast.success(
        variables.enabled ? `${label} is now disposable` : `Disposable mode cleared for ${label}`,
        { duration: TOAST_SUCCESS_DURATION_MS },
      );
    },
    onError: (error, variables) => {
      toast.error(
        `Failed to ${variables.enabled ? "mark" : "unmark"} ${machineLabel(variables.name, variables.id)} disposable`,
        { description: errorMessage(error), duration: TOAST_ERROR_DURATION_MS },
      );
    },
  });
}

// Replaces the allowlist (empty = anyone) and takeover window (null = now) together;
// the same POST also marks the machine disposable — one endpoint owns both.
export function useSetDisposableOptions() {
  const qc = useQueryClient();
  const { formatShortTimestamp } = useTimezone();
  return useMutation({
    mutationFn: ({
      id,
      allowedUserIds,
      disposableFrom,
    }: {
      id: number;
      allowedUserIds: number[];
      disposableFrom: string | null;
      name?: string;
    }) => api.setDisposableOptions(id, { allowedUserIds, disposableFrom }),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      const label = machineLabel(variables.name ?? data.name, data.id);
      const count = variables.allowedUserIds.length;
      const who = count === 0 ? "anyone" : `${count} selected user${count === 1 ? "" : "s"}`;
      const from = variables.disposableFrom
        ? ` from ${formatShortTimestamp(variables.disposableFrom)}`
        : "";
      toast.success(`${label} is disposable to ${who}${from}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      toast.error(
        `Failed to update disposable options for ${machineLabel(variables.name, variables.id)}`,
        {
          description: errorMessage(error),
          duration: TOAST_ERROR_DURATION_MS,
        },
      );
    },
  });
}

/**
 * Toggle the nightly reaper's per-machine opt-out (`skip_delete`).
 *
 * Protecting is open to any user; un-protecting is admin-only server-side
 * (it re-arms an irreversible delete), so the disable path can 403.
 */
export function useToggleSkipDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean; name?: string }) =>
      enabled ? api.protectMachine(id) : api.unprotectMachine(id),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      const label = machineLabel(variables.name ?? data.name, data.id);
      toast.success(
        variables.enabled
          ? `Protected ${label} from the nightly reaper`
          : `Removed reaper protection from ${label}`,
        { duration: TOAST_SUCCESS_DURATION_MS },
      );
    },
    onError: (error, variables) => {
      const verb = variables.enabled ? "protect" : "unprotect";
      toast.error(`Failed to ${verb} ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useMarkMachineProduction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; name?: string }) => api.markMachineProduction(id),
    onSuccess: (_data, variables) => {
      invalidateMachines(qc);
      void qc.invalidateQueries({ queryKey: ["production-machines"] });
      toast.success(
        `Marked ${machineLabel(variables.name, variables.id)} as production — removed from Fleet`,
        { duration: TOAST_SUCCESS_DURATION_MS },
      );
    },
    onError: (error, variables) => {
      toast.error(`Failed to mark ${machineLabel(variables.name, variables.id)} production`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useAssignInferbot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; name?: string }) => api.assignInferbot(id),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      toast.success(`Assigned ${machineLabel(variables.name ?? data.name, data.id)} to inferbot`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      toast.error(`Failed to assign ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useBatchAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      machineIds,
      action,
      deleteAfterStop,
    }: {
      machineIds: number[];
      action: BatchActionName;
      deleteAfterStop?: boolean;
    }) => api.batchAction(machineIds, action, { deleteAfterStop }),
    onSuccess: () => invalidateMachines(qc),
    // Per-machine outcomes (and the "Released N" success toast) are surfaced
    // by BatchProgressDialog. A network/422 mutation error is rendered in
    // the dialog as well — no toast needed here.
  });
}

export function useRebootMachine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; name?: string }) => api.rebootMachine(id),
    onSuccess: (data: Machine, variables) => {
      invalidateMachines(qc);
      toast.success(`Rebooted ${machineLabel(variables.name ?? data.name, data.id)}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      toast.error(`Failed to reboot ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

function invalidateMachineRequests(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["machine-requests"] });
}

export function useMyMachineRequests() {
  return useQuery({
    queryKey: ["machine-requests"],
    queryFn: () => api.listMyMachineRequests(true),
    refetchInterval: 30_000,
  });
}

export function useRequestMachine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; name?: string }) => api.requestMachine(id),
    onSuccess: (request: MachineRequest, variables) => {
      invalidateMachineRequests(qc);
      toast.success(
        `Requested ${machineLabel(variables.name ?? request.machine_name, variables.id)}`,
        {
          description: `Owner has 30 minutes to accept, deny, or pick a release time. You'll get the machine if they don't respond.`,
          duration: TOAST_SUCCESS_DURATION_MS,
        },
      );
    },
    onError: (error, variables) => {
      toast.error(`Failed to request ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useRequestMachinesBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ machineIds }: { machineIds: number[] }) => api.requestMachinesBatch(machineIds),
    onSuccess: () => invalidateMachineRequests(qc),
  });
}

export function useCancelMachineRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId }: { requestId: number; machineName?: string }) =>
      api.cancelMachineRequest(requestId),
    onSuccess: (_data, variables) => {
      invalidateMachineRequests(qc);
      const label = variables.machineName ?? `request #${variables.requestId}`;
      toast.success(`Cancelled request for ${label}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, _variables) => {
      toast.error(`Failed to cancel request`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useDeleteMachine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; name?: string }) => api.deleteMachine(id),
    onSuccess: (_data, variables) => {
      invalidateMachines(qc);
      toast.success(`Deleted ${machineLabel(variables.name, variables.id)}`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error, variables) => {
      toast.error(`Failed to delete ${machineLabel(variables.name, variables.id)}`, {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}
